import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Database, Trash2, AlertTriangle, Loader2, RefreshCw, Server, Clock, Cpu, HardDrive } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Deletion categories with their descriptions
const DELETE_OPTIONS = [
  { key: "holdings", label: "Holdings Data", desc: "holdings_enriched collection" },
  { key: "funds", label: "Fund Data", desc: "funds_master, fund_quarter_status" },
  { key: "fundperf", label: "Fund Performance Data", desc: "fund_performance_quarters" },
  { key: "runs", label: "Pipeline Run History", desc: "pipeline_runs, pipeline_logs, error_events" },
  { key: "buysell", label: "Buy/Sell Events", desc: "fund_buy_events, fund_sell_events, state" },
  { key: "stockperf", label: "Stock Performance Data", desc: "stock_perf_daily_raw, universe, runs, state" },
  { key: "companyprofiles", label: "Company Profiles", desc: "company_profiles_fmp, pipeline state" },
  { key: "pricetargets", label: "Consensus / Price Targets", desc: "price_target_daily, runs, state" },
  { key: "alpha", label: "Alpha Data", desc: "stock_alpha_daily, config, runs, state" },
  { key: "factors", label: "Factor Loadings", desc: "stock_factor_loadings, factor prices/returns, config, state" },
  { key: "fundintelligence", label: "Fund Intelligence", desc: "skill estimates, timing curves, validated funds, runs, logs" },
  { key: "centrality", label: "Centrality Data", desc: "stock_centrality, config, state, runs, logs" },
  { key: "curves", label: "Performance Curves", desc: "fund_curves, cluster_centers, config, state, runs, logs" },
  { key: "kensystargets", label: "Kensys Target Prices", desc: "kensys_target_prices, runs, logs, config, state" },
  { key: "dynamicupside", label: "Dynamic Upside", desc: "dynamic_upside_daily, config" },
  { key: "nowcasting", label: "Nowcasting", desc: "stock_nowcasting, fund_level, runs, logs, config" },
  { key: "caches", label: "Resolution Caches", desc: "CUSIP resolution, symbol profiles, scheduled runs" },
  { key: "dataquality", label: "Data Quality Issues", desc: "unresolved CUSIPs, missing profiles" },
  { key: "configs", label: "Configurations", desc: "All saved pipeline configurations" },
];

const DatabasePage = () => {
  const { token } = useAuth();
  const [selectedItems, setSelectedItems] = useState([]);
  const [eraseConfirmation, setEraseConfirmation] = useState("");
  const [erasing, setErasing] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [serverStatus, setServerStatus] = useState(null);

  const fetchServerStatus = async () => {
    try {
      const response = await fetch(`${API}/config/server-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setServerStatus(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch server status:", error);
    }
  };

  useEffect(() => {
    fetchServerStatus();
    const interval = setInterval(fetchServerStatus, 10000);
    return () => clearInterval(interval);
  }, [token]);

  const handleRestartServer = async () => {
    if (!window.confirm("Are you sure you want to restart the server? The app will be briefly unavailable.")) {
      return;
    }
    
    setRestarting(true);
    try {
      const response = await fetch(`${API}/config/restart-server?confirmation=RESTART`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Server restart initiated. Please wait...");
        // Wait a bit and then start checking if server is back
        setTimeout(() => {
          const checkServer = setInterval(async () => {
            try {
              const res = await fetch(`${API}/config/server-status`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (res.ok) {
                clearInterval(checkServer);
                setRestarting(false);
                toast.success("Server restarted successfully!");
                fetchServerStatus();
              }
            } catch {
              // Server still restarting
            }
          }, 1000);
          
          // Stop checking after 30 seconds
          setTimeout(() => {
            clearInterval(checkServer);
            setRestarting(false);
          }, 30000);
        }, 2000);
      } else {
        toast.error("Failed to restart server");
        setRestarting(false);
      }
    } catch (error) {
      toast.error("Failed to restart server");
      setRestarting(false);
    }
  };

  const toggleItem = (key) => {
    setSelectedItems(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const selectAll = () => {
    setSelectedItems(DELETE_OPTIONS.map(o => o.key));
  };

  const selectNone = () => {
    setSelectedItems([]);
  };

  const handleResetDatabase = async () => {
    if (eraseConfirmation !== "ERASE") {
      toast.error("Type ERASE to confirm");
      return;
    }

    if (selectedItems.length === 0) {
      toast.error("Select at least one category to delete");
      return;
    }

    setErasing(true);
    try {
      // Call reset for each selected item
      for (const item of selectedItems) {
        toast.info(`Deleting ${item}... (this may take a while for large collections)`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minute timeout
        
        try {
          const response = await fetch(
            `${API}/config/reset-database?confirmation=ERASE&reset_type=${item}`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal
            }
          );
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Failed to delete ${item}: HTTP ${response.status}`);
          }
          
          const result = await response.json();
          console.log(`Reset result for ${item}:`, result);
          toast.success(`Deleted ${item}: ${JSON.stringify(result.deleted || {})}`);
          
        } catch (fetchError) {
          if (fetchError.name === 'AbortError') {
            throw new Error(`Timeout deleting ${item} - collection may be too large. Try again or delete directly in MongoDB Atlas.`);
          }
          throw fetchError;
        }
      }

      toast.success(`All selected items deleted successfully`);
      setEraseConfirmation("");
      setSelectedItems([]);
    } catch (error) {
      console.error("Reset error:", error);
      toast.error(error.message || "Failed to reset database");
    } finally {
      setErasing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="database-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Database Management</h1>
        <p className="text-sm text-muted-foreground">
          Manage and reset database collections
        </p>
      </div>

      {/* Server Status Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-green-500" />
              <CardTitle className="text-lg">Server Status</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchServerStatus}
                disabled={restarting}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${restarting ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleRestartServer}
                disabled={restarting}
              >
                {restarting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Restarting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Restart Server
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {serverStatus ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  Status
                </div>
                <p className="font-medium capitalize">{serverStatus.status}</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <Clock className="w-3 h-3" />
                  Uptime
                </div>
                <p className="font-medium">
                  {serverStatus.uptime_seconds > 3600 
                    ? `${Math.floor(serverStatus.uptime_seconds / 3600)}h ${Math.floor((serverStatus.uptime_seconds % 3600) / 60)}m`
                    : `${Math.floor(serverStatus.uptime_seconds / 60)}m ${Math.floor(serverStatus.uptime_seconds % 60)}s`}
                </p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <HardDrive className="w-3 h-3" />
                  Memory
                </div>
                <p className="font-medium">{serverStatus.memory_mb?.toFixed(0)} MB</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <Cpu className="w-3 h-3" />
                  PID
                </div>
                <p className="font-medium">{serverStatus.pid}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-4 text-muted-foreground">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading server status...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cold Storage (R2 / DuckDB) — hybrid hot/cold datastore */}
      <ColdStorageCard token={token} />

      {/* Database Reset with Checkboxes */}
      <Card className="bg-card border-border rounded-sm border-red-500/30">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2 text-red-400">
            <Trash2 className="w-4 h-4" />
            Erase Database
          </CardTitle>
          <CardDescription>
            Select collections to permanently delete. This cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Warning */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-400">Danger Zone</p>
                <p className="text-xs text-muted-foreground mt-1">
                  This action will permanently delete data. Make sure you have backups if needed.
                </p>
              </div>
            </div>
          </div>

          {/* Selection Controls */}
          <div className="flex items-center gap-4">
            <Label className="text-sm font-medium">Select what to delete:</Label>
            <Button variant="outline" size="sm" onClick={selectAll} className="text-xs rounded-sm">
              Select All
            </Button>
            <Button variant="outline" size="sm" onClick={selectNone} className="text-xs rounded-sm">
              Clear All
            </Button>
          </div>

          {/* Checkboxes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {DELETE_OPTIONS.map((option) => (
              <label
                key={option.key}
                className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                  selectedItems.includes(option.key)
                    ? "bg-red-500/10 border-red-500/50"
                    : "bg-background/50 border-border hover:border-muted-foreground"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedItems.includes(option.key)}
                  onChange={() => toggleItem(option.key)}
                  className="mt-1 rounded border-border"
                />
                <div>
                  <p className="text-sm font-medium">{option.label}</p>
                  <p className="text-xs text-muted-foreground">{option.desc}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Selected Summary */}
          {selectedItems.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-sm p-3">
              <p className="text-sm text-red-400">
                Will delete: <span className="font-mono">{selectedItems.join(", ")}</span>
              </p>
            </div>
          )}

          {/* Confirmation */}
          <div>
            <Label className="text-sm font-medium">Type ERASE to confirm</Label>
            <Input
              value={eraseConfirmation}
              onChange={(e) => setEraseConfirmation(e.target.value)}
              placeholder="ERASE"
              className="mt-2 bg-background rounded-sm font-mono"
            />
          </div>

          {/* Submit */}
          <Button
            variant="destructive"
            onClick={handleResetDatabase}
            disabled={eraseConfirmation !== "ERASE" || erasing || selectedItems.length === 0}
            className="w-full rounded-sm"
            data-testid="reset-database-btn"
          >
            {erasing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Erase {selectedItems.length} Selected {selectedItems.length === 1 ? "Category" : "Categories"}
          </Button>
        </CardContent>
      </Card>

      {/* Collections Reference */}
      <Card className="bg-card border-border rounded-sm">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Database className="w-4 h-4" />
            Collections Reference
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
            <div className="space-y-2">
              <p className="font-medium text-muted-foreground">13F Holdings</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• pipeline_configs</li>
                <li>• pipeline_runs</li>
                <li>• funds_master</li>
                <li>• fund_quarter_status</li>
                <li>• holdings_enriched</li>
                <li>• fund_buy_events</li>
                <li>• fund_sell_events</li>
                <li>• fund_performance_quarters</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-medium text-muted-foreground">Company Profiles</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• company_profiles_fmp</li>
                <li>• company_profile_pipeline_state</li>
              </ul>
              <p className="font-medium text-muted-foreground mt-4">Stock Performance</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• stock_universe_fmp</li>
                <li>• stock_perf_daily_raw</li>
                <li>• stock_perf_pipeline_runs</li>
                <li>• stock_perf_pipeline_state</li>
                <li>• stock_perf_failed_symbols</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-medium text-muted-foreground">Alpha & Factors</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• stock_alpha_daily</li>
                <li>• alpha_pipeline_runs</li>
                <li>• alpha_pipeline_state</li>
                <li>• stock_factor_loadings</li>
                <li>• factor_prices_daily</li>
                <li>• factor_returns_daily</li>
                <li>• factor_pipeline_runs</li>
              </ul>
              <p className="font-medium text-muted-foreground mt-4">Fund Intelligence</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• fund_sector_skill_12m</li>
                <li>• fund_sector_skill_6m</li>
                <li>• fund_sector_curve</li>
                <li>• fund_sector_table</li>
                <li>• validated_fund_universe</li>
                <li>• fund_intelligence_runs</li>
                <li>• fund_intelligence_logs</li>
                <li>• fund_intelligence_state</li>
                <li>• fund_intelligence_config</li>
              </ul>
            </div>
            <div className="space-y-2">
              <p className="font-medium text-muted-foreground">Caches & Support</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• cusip_resolution_cache</li>
                <li>• symbol_profile_cache</li>
                <li>• scheduled_runs</li>
                <li>• error_events</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DatabasePage;


/* ============================================================
   ColdStorageCard — visibility + diagnostics for the hybrid
   hot (MongoDB) / cold (Cloudflare R2 via DuckDB) datastore.
   ============================================================ */
function ColdStorageCard({ token }) {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [symbol, setSymbol] = useState("NVDA");
  const [obsDate, setObsDate] = useState("2024-09-13");
  const [verifyResult, setVerifyResult] = useState(null); // { source, rows, took_ms }
  const [verifying, setVerifying] = useState(false);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const [s, l] = await Promise.all([
        fetch(`${API}/cold-archive/status`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/cold-archive/log`,    { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (s.ok) setStatus(await s.json());
      if (l.ok) setLog(await l.json());
    } catch (e) {
      console.error("cold-archive fetch failed:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const runVerify = async (forceCold) => {
    setVerifying(true);
    setVerifyResult(null);
    const t0 = performance.now();
    try {
      const url = `${API}/nowcasting/data/${symbol.trim().toUpperCase()}/funds?observation_date=${obsDate}${forceCold ? "&force_cold=true" : ""}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const took = Math.round(performance.now() - t0);
      const source = r.headers.get("X-Data-Source") || "?";
      const body = r.ok ? await r.json() : [];
      setVerifyResult({ source, rows: body.length, took_ms: took, forced: forceCold });
    } catch (e) {
      setVerifyResult({ source: "error", rows: 0, took_ms: 0, error: String(e) });
    } finally {
      setVerifying(false);
    }
  };

  const sizeMb = status?.archive?.total_size_mb;
  const partitions = status?.archive?.total_partitions;

  return (
    <Card data-testid="cold-storage-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-cyan-400" />
            <CardTitle className="text-lg">Cold Storage (Cloudflare R2)</CardTitle>
          </div>
          <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loading} data-testid="cold-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
        <CardDescription>
          MongoDB holds the last 90 days (hot). Older partitions live as Parquet on R2 and are queried via DuckDB.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Configured" value={status?.configured ? "✓ yes" : "✗ no"} tone={status?.configured ? "good" : "bad"} />
          <Stat label="Partitions on R2" value={partitions ?? "—"} testid="cold-partitions" />
          <Stat label="R2 footprint" value={sizeMb != null ? `${sizeMb.toFixed(1)} MB` : "—"} testid="cold-size" />
          <Stat
            label="Nightly cron"
            value={status?.cron?.registered ? (status?.cron?.schedule || "registered") : "off"}
            tone={status?.cron?.registered ? "good" : "warn"}
            testid="cold-cron-status"
          />
        </div>

        {/* Verification panel */}
        <div className="border border-border/40 rounded-sm p-3 space-y-3 bg-muted/20">
          <div className="text-sm font-semibold">Verify R2 read path</div>
          <div className="text-xs text-muted-foreground">
            Pick any (symbol, observation_date). Use <span className="font-mono">Verify cold path</span> to force the R2/DuckDB read.
            The <span className="font-mono">X-Data-Source</span> response header is shown below.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Symbol</Label>
              <Input
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                placeholder="NVDA"
                data-testid="cold-verify-symbol"
              />
            </div>
            <div>
              <Label className="text-xs">Observation date (YYYY-MM-DD)</Label>
              <Input
                value={obsDate}
                onChange={(e) => setObsDate(e.target.value)}
                placeholder="2024-09-13"
                data-testid="cold-verify-date"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => runVerify(false)}
                disabled={verifying}
                className="flex-1"
                data-testid="cold-verify-default-btn"
              >
                {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : "Default (hot first)"}
              </Button>
              <Button
                size="sm"
                onClick={() => runVerify(true)}
                disabled={verifying}
                className="flex-1"
                data-testid="cold-verify-force-btn"
              >
                {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : "Verify cold path"}
              </Button>
            </div>
          </div>
          {verifyResult && (
            <div
              className="text-xs font-mono p-2 rounded-sm bg-background/60 border border-border/40 flex items-center gap-3 flex-wrap"
              data-testid="cold-verify-result"
            >
              <span>
                source ={" "}
                <span
                  className={
                    verifyResult.source === "cold" ? "text-cyan-300 font-bold" :
                    verifyResult.source === "hot"  ? "text-orange-300 font-bold" :
                                                     "text-muted-foreground"
                  }
                >
                  {verifyResult.source}
                </span>
              </span>
              <span>rows = {verifyResult.rows}</span>
              <span>took = {verifyResult.took_ms} ms</span>
              {verifyResult.forced && <span className="text-cyan-400">force_cold=true</span>}
              {verifyResult.error && <span className="text-red-400">{verifyResult.error}</span>}
            </div>
          )}
        </div>

        {/* Archive log table */}
        {log.length > 0 && (
          <div className="space-y-2" data-testid="cold-log">
            <div className="text-sm font-semibold">Archived partitions ({log.length})</div>
            <div className="max-h-60 overflow-y-auto border border-border/40 rounded-sm">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Collection</th>
                    <th className="text-left px-2 py-1">Partition</th>
                    <th className="text-right px-2 py-1">Rows</th>
                    <th className="text-right px-2 py-1">Parquet (MB)</th>
                    <th className="text-left px-2 py-1">Archived</th>
                    <th className="text-left px-2 py-1">Mongo</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((r) => (
                    <tr key={`${r.collection}-${r.year}-${r.quarter}`} className="border-t border-border/30">
                      <td className="px-2 py-1 font-mono">{r.collection}</td>
                      <td className="px-2 py-1">{r.year}Q{r.quarter}</td>
                      <td className="px-2 py-1 text-right font-mono">{r.rows?.toLocaleString()}</td>
                      <td className="px-2 py-1 text-right font-mono">{(r.parquet_bytes / 1024 / 1024).toFixed(1)}</td>
                      <td className="px-2 py-1 text-muted-foreground">{r.archived_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-2 py-1">
                        {r.deleted_from_hot
                          ? <span className="text-red-400">deleted</span>
                          : <span className="text-green-400">intact</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function Stat({ label, value, tone, testid }) {
  const toneCls =
    tone === "good" ? "text-green-400" :
    tone === "bad"  ? "text-red-400"   :
    tone === "warn" ? "text-yellow-300" :
                      "text-foreground";
  return (
    <div className="p-3 bg-muted/30 rounded-sm" data-testid={testid}>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className={`text-sm font-mono font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}
